import React, { useState, useEffect } from 'react';
import { ContentField } from '../api/section.api';
import ImageUpload from './ImageUpload';
import RichTextEditor from './RichTextEditor';

interface ContentEditorProps {
  fields: ContentField[];
  initialContent: Record<string, any>;
  onSave: (content: Record<string, any>) => void;
  isSaving: boolean;
}

const ContentEditor: React.FC<ContentEditorProps> = ({ fields, initialContent, onSave, isSaving }) => {
  const [content, setContent] = useState<Record<string, any>>(initialContent || {});

  useEffect(() => {
    setContent(initialContent || {});
  }, [initialContent]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setContent((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleArrayFieldChange = (fieldName: string, index: number, subFieldName: string, value: any) => {
    const currentArray = content[fieldName] || [];
    const updatedArray = [...currentArray];
    updatedArray[index] = {
      ...updatedArray[index],
      [subFieldName]: value,
    };
    setContent((prev) => ({
      ...prev,
      [fieldName]: updatedArray,
    }));
  };

  const handleAddArrayItem = (fieldName: string) => {
    const currentArray = content[fieldName] || [];
    setContent((prev) => ({
      ...prev,
      [fieldName]: [...currentArray, {}],
    }));
  };

  const handleRemoveArrayItem = (fieldName: string, index: number) => {
    const currentArray = content[fieldName] || [];
    const updatedArray = currentArray.filter((_: any, i: number) => i !== index);
    setContent((prev) => ({
      ...prev,
      [fieldName]: updatedArray,
    }));
  };

  const renderField = (field: ContentField) => {
    const value = content[field.name] || '';

    switch (field.type) {
      case 'text':
      case 'url':
      case 'email':
      case 'phone':
        return (
          <input
            type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
          />
        );

      case 'richtext':
        return (
          <RichTextEditor
            value={value}
            onChange={(html) => handleFieldChange(field.name, html)}
            placeholder={field.placeholder}
          />
        );

      case 'image':
        return (
          <ImageUpload
            value={value}
            onChange={(url) => handleFieldChange(field.name, url)}
            label="Upload image or paste URL"
            folder="website-images"
            recommendedSize={field.recommendedSize}
          />
        );

      case 'array':
        const arrayValue = content[field.name] || [];
        return (
          <div className="space-y-4">
            {arrayValue.map((item: any, index: number) => (
              <div key={index} className="p-4 border border-nova-border rounded-lg bg-nova-bg2">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-nova-text2">
                    Item {index + 1}
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleRemoveArrayItem(field.name, index)}
                    className="text-nova-red hover:text-nova-red/80"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
                <div className="space-y-3">
                  {field.arrayFields?.map((subField) => (
                    <div key={subField.name}>
                      <label className="block text-sm font-medium text-nova-text2 mb-1">
                        {subField.label}
                        {subField.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {subField.type === 'textarea' ? (
                        <textarea
                          value={item[subField.name] || ''}
                          onChange={(e) =>
                            handleArrayFieldChange(field.name, index, subField.name, e.target.value)
                          }
                          placeholder={subField.placeholder}
                          rows={3}
                          className="w-full px-3 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
                        />
                      ) : subField.type === 'image' ? (
                        <ImageUpload
                          value={item[subField.name] || ''}
                          onChange={(url) =>
                            handleArrayFieldChange(field.name, index, subField.name, url)
                          }
                          label="Upload image or paste URL"
                          folder="website-images"
                          recommendedSize={subField.recommendedSize}
                        />
                      ) : (
                        <input
                          type={subField.type === 'email' ? 'email' : subField.type === 'phone' ? 'tel' : 'text'}
                          value={item[subField.name] || ''}
                          onChange={(e) =>
                            handleArrayFieldChange(field.name, index, subField.name, e.target.value)
                          }
                          placeholder={subField.placeholder}
                          className="w-full px-3 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => handleAddArrayItem(field.name)}
              className="w-full py-2 border-2 border-dashed border-nova-border rounded-lg text-nova-text2 hover:border-nova-sky hover:text-nova-sky transition-colors"
            >
              + Add {field.label}
            </button>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
          />
        );
    }
  };

  return (
    <div className="space-y-6">
      {fields.map((field) => (
        <div key={field.name}>
          <label className="block text-sm font-semibold text-nova-text2 mb-2">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {renderField(field)}
        </div>
      ))}

      <div className="pt-4 border-t border-nova-border flex justify-end">
        <button
          onClick={() => onSave(content)}
          disabled={isSaving}
          className="px-6 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Content'}
        </button>
      </div>
    </div>
  );
};

export default ContentEditor;
